import { SortingState } from '@ui-components-3/ui/lib/components/Table/types'
import { useCallback, useEffect, useState } from 'react'
import { PaginatedResponse, PaginationRequestParams } from 'types/api'
import debounce from 'lodash/debounce'
import { cleanFilters, cleanSort } from './utils'

export type PaginationState = {
  pageSize: number
  pageIndex: 0
}

const calcPage = ({ pageSize, pageIndex }: PaginationState) => ({
  limit: pageSize,
  skip: pageIndex === 0 ? 0 : pageIndex * pageSize,
})

export const useFetchServerPaginationTableData = <
  DataType extends Record<string, any>,
  FiltersType extends Record<string, string | null>,
>({
  filters,
  fetchDataRequest,
  shouldFetch = true,
  initialPagination,
}: {
  filters?: FiltersType
  fetchDataRequest: (params: PaginationRequestParams<FiltersType>) => Promise<PaginatedResponse<DataType>>
  shouldFetch?: boolean
  initialPagination?: PaginationState
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [sorting, setSorting] = useState<SortingState>([])
  const [pagination, setPagination] = useState<PaginationState>(() => ({
    pageSize: 25,
    pageIndex: 0,
    ...initialPagination,
  }))

  const [data, setData] = useState<PaginatedResponse<DataType>>({
    paginated: [],
    meta: { results: 0 },
  })

  const [debouncedFilters, setDebouncedFilters] = useState(filters)

  useEffect(() => {
    const debounceFilters = debounce(() => {
      setDebouncedFilters(filters)
    }, 500)

    debounceFilters()

    return () => {
      debounceFilters.cancel()
    }
  }, [filters])

  const fetchData = useCallback(async () => {
    setIsLoading(true)
    const _data = await fetchDataRequest({
      page: calcPage(pagination),
      sort: cleanSort(sorting[0]),
      filter: cleanFilters(debouncedFilters),
    })
    setData(_data)
    setIsLoading(false)
  }, [debouncedFilters, fetchDataRequest, pagination, sorting])

  useEffect(() => {
    if (shouldFetch) {
      fetchData()
    }
  }, [fetchData, shouldFetch])

  return {
    data,
    isLoading,
    pagination,
    setPagination,
    sorting,
    setSorting,
  }
}
